import React, { useEffect, useRef, useState } from "react";
import { Input, Button, Modal, Spin } from "antd";
import { useAppointmentRequestSrv } from "./useAppointmentRequestSrv";
import "./AppointmentRequest.css";
import { handleErrorNavigation } from "pages/ErrorPage/ErrorHandler";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import { usePageTitle } from "helpers/usePageTitle";


const TIME_SLOTS = [
  "9:00 AM – 11:00 AM",
  "11:00 AM – 1:00 PM",
  "1:00 PM – 3:00 PM",
  "3:00 PM – 5:00 PM",
];

const MAX_SELECTIONS = 3;

type AppointmentFormData = {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  guardianName: string;
  guardianContact: string;
  pcpName: string;
  pcpContact: string;
};

type AppointmentItem = {
  iso: string;
  date: string;
  slots: string[];
};

const getSlotLabelFromStartTime = (startTime: string): string | undefined => {
  const timeString = startTime.split("T").pop()?.replace("Z", "") || startTime;
  const hour = parseInt(timeString.slice(0, 2), 10);
  if (hour === 9) return TIME_SLOTS[0];
  if (hour === 11) return TIME_SLOTS[1];
  if (hour === 13 || hour === 14) return TIME_SLOTS[2];
  if (hour === 15) return TIME_SLOTS[3];
  return undefined;
};

export default function AppointmentRequest() {
  const { getAppointmentDetails, submitAppointmentRequest } = useAppointmentRequestSrv();

  const [formData, setFormData] = useState<AppointmentFormData>({
    firstName: "",
    lastName: "",
    email: "",
    dateOfBirth: "",
    guardianName: "",
    guardianContact: "",
    pcpName: "",
    pcpContact: "",
  });
  const [patientId, setPatientId] = useState<string | undefined>(undefined);
  const [isMinor, setIsMinor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlots, setSelectedSlots] = useState<number[]>([]);
  const [appointments, setAppointments] = useState<AppointmentItem[]>([]);
  const [authorized, setAuthorized] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const guardianNameRef = useRef<any>(null);
  const guardianContactRef = useRef<any>(null);
  const navigate = useNavigate();
  usePageTitle("APPOINTMENT.PAGE_TITLE", `AppointmentRequest - ${process.env.REACT_APP_PRODUCT}`);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const minDate = tomorrow.toISOString().split("T")[0];

  const handleChange = (field: keyof AppointmentFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleAddSelection = () => {
    if (!selectedDate) {
      Modal.info({
        title: "This page says",
        content: "Please select a date.",
        okText: "OK",
        icon: null,
        okButtonProps: { style: { backgroundColor: "#6d4de6", borderColor: "#6d4de6", color: "#fff" } },
      });
      return;
    }
    if (!selectedSlots || selectedSlots.length === 0) {
      Modal.info({
        title: "This page says",
        content: "Please select at least one time slot.",
        okText: "OK",
        icon: null,
        okButtonProps: { style: { backgroundColor: "#6d4de6", borderColor: "#6d4de6", color: "#fff" } },
      });
      return;
    }
    if (appointments.length >= MAX_SELECTIONS) {
      Modal.info({
        title: "This page says",
        content: `You can add up to ${MAX_SELECTIONS} dates.`,
        okText: "OK",
        icon: null,
        okButtonProps: { style: { backgroundColor: "#6d4de6", borderColor: "#6d4de6", color: "#fff" } },
      });
      return;
    }
    // prevent duplicate date (by ISO date)
    if (appointments.some((a) => a.iso === selectedDate)) {
      Modal.info({
        title: "This page says",
        content: "Duplicate appointment is not allowed. This date has already been added.",
        okText: "OK",
        icon: null,
        okButtonProps: { style: { backgroundColor: "#6d4de6", borderColor: "#6d4de6", color: "#fff" } },
      });
      return;
    }

    // format date for display: Thu, 2 Jul 2026
    const d = new Date(selectedDate + "T00:00:00");
    const formatted = new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(d);

    setAppointments((prev) => [
      ...prev,
      { iso: selectedDate, date: formatted, slots: selectedSlots.map((i) => TIME_SLOTS[i]) },
    ]);
    setSelectedDate("");
    setSelectedSlots([]);
  };

  const handleRemove = (index: number) => {
    setAppointments((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getAppointmentDetails();
        if (result?.data) {
          setPatientId(result.data.PatientID || result.data.patientID || result.data.patientId);
          setIsMinor(result.data.IsMinor === true);
          setFormData((prev) => ({
            ...prev,
            firstName: result.data.FirstName || result.data.firstName || prev.firstName,
            lastName: result.data.LastName || result.data.lastName || prev.lastName,
            email: result.data.EmailID || result.data.emailID || result.data.email || result.data.email_id || prev.email,
            dateOfBirth: result.data.DateOfBirth ? result.data.DateOfBirth.split("T")[0] : prev.dateOfBirth,
            guardianName: result.data.GuardianName || result.data.guardianName || result.data.guardian_name || prev.guardianName,
            guardianContact: result.data.GuardianContactNumber || result.data.guardianContactNumber || result.data.guardian_contact_number || prev.guardianContact,
            pcpName: result.data.PCPName || result.data.providerName || result.data.provider_name || prev.pcpName,
            pcpContact: result.data.PCPContactNumber || result.data.providerContactNumber || result.data.provider_contact_number || prev.pcpContact,
          }));

          const preferences = result.data.appointmentReqPreferences || result.data.AppointmentReqPreferences;
          if (Array.isArray(preferences) && preferences.length > 0) {
            const grouped: Record<string, string[]> = {};
            preferences.forEach((pref: any) => {
              const preferredDate = pref.PreferredDate || pref.preferredDate;
              if (!preferredDate) return;
              const dateOnly = preferredDate.split("T")[0];
              const slotLabel = getSlotLabelFromStartTime(pref.StartTime || pref.startTime || "");
              if (!slotLabel) return;
              grouped[dateOnly] = grouped[dateOnly] || [];
              if (!grouped[dateOnly].includes(slotLabel)) {
                grouped[dateOnly].push(slotLabel);
              }
            });
            const loadedAppointments = Object.entries(grouped).map(([iso, slots]) => {
              const d = new Date(iso + "T00:00:00");
              const formatted = new Intl.DateTimeFormat("en-GB", {
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric",
              }).format(d);
              return { iso, date: formatted, slots };
            });
            setAppointments(loadedAppointments);
          }
        }
      } catch (err: any) {
        console.error(err);
        handleErrorNavigation(err, navigate, t);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    // show validation styles only after the user attempts to submit
    setShowValidationErrors(true);
    if (!authorized) return;

    if (isMinor && (!formData.guardianName.trim() || !formData.guardianContact.trim())) {
      setValidationError("Guardian Name and Guardian Contact Number are required when the member is a minor.");

      const missingField = !formData.guardianName.trim() ? guardianNameRef.current : guardianContactRef.current;
      const inputElement = missingField?.input ?? missingField?.resizableTextArea?.textArea ?? missingField;
      if (inputElement) {
        inputElement.scrollIntoView?.({ behavior: "smooth", block: "center" });
        setTimeout(() => inputElement.focus?.(), 250);
      }
      return;
    }

    if (!appointments || appointments.length === 0) {
      Modal.info({
        title: "This page says",
        content: "Please add at least one appointment selection before submitting.",
        okText: "OK",
      });
      return;
    }

    if (!patientId) {
      Modal.error({
        title: "Unable to submit",
        content: "Patient information is not available. Please refresh the page and try again.",
        okText: "OK",
      });
      return;
    }

    const slotTimeMap: Record<string, { start: string; end: string }> = {
      "9:00 AM – 11:00 AM": { start: "09:00:00", end: "10:00:00" },
      "11:00 AM – 1:00 PM": { start: "11:00:00", end: "12:00:00" },
      "1:00 PM – 3:00 PM": { start: "13:00:00", end: "14:00:00" },
      "3:00 PM – 5:00 PM": { start: "15:00:00", end: "16:00:00" },
    };

    setSubmitting(true);

    const payload = {
      patientID: patientId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      dateOfBirth: formData.dateOfBirth,
      emailID: formData.email,
      guardianName: formData.guardianName,
      guardianContactNumber: formData.guardianContact,
      pcpName: formData.pcpName,
      pcpContactNumber: formData.pcpContact,
      appointmentReqPreferences: appointments.flatMap((item, index) =>
        item.slots.map((slot, slotIndex) => {
          const timeRange = slotTimeMap[slot] || { start: "09:00:00", end: "10:00:00" };
          return {
            preferenceOrder: index + 1,
            preferredDate: item.iso,
            startTime: `${item.iso}T${timeRange.start}`,
            endTime: `${item.iso}T${timeRange.end}`,
          };
        })
      ),
    };

    try {
      await submitAppointmentRequest(payload);
      Modal.success({
        title: "Success",
        content: "Your appointment request has been submitted successfully.",
        okText: "OK",
      });
      // clear validation markers after successful submit
      setShowValidationErrors(false);
    } catch (error) {
      console.error(error);
      handleErrorNavigation(error, navigate, t);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="hc-page-loader-wrapper">
      <Spin spinning={loading || submitting} size="large">
        <div className="hc-card">
          <h1 id="appointment-request-title" tabIndex={0} className="hc-title">Appointment Request</h1>
          {error && <div className="hc-error">{error}</div>}

          {/* Member Information */}
          <section className="hc-section">
            <h2 id="member-information" tabIndex={0} className="hc-section-title">Member Information</h2>
            <div className="hc-grid-2">
              <div className="hc-field">
                <label id="first-name-label" htmlFor="first-name" tabIndex={0} className="hc-label">First Name</label>
                <Input
                  type="text"
                  id="first-name"
                  aria-label="first-name"
                  tabIndex={0}
                  className="hc-input"
                  value={formData.firstName}
                  onChange={handleChange("firstName")}
                  disabled
                />
              </div>
              <div className="hc-field">
                <label id="last-name-label" htmlFor="last-name" tabIndex={0} className="hc-label">Last Name</label>
                <Input
                  type="text"
                  id="last-name"
                  aria-label="last-name"
                  tabIndex={0}
                  className="hc-input"
                  value={formData.lastName}
                  onChange={handleChange("lastName")}
                  disabled
                />
              </div>
              <div className="hc-field">
                <label id="email-label" htmlFor="email-input" tabIndex={0} className="hc-label">Email</label>
                <Input
                  type="email"
                  id="email-input"
                  aria-label="email"
                  tabIndex={0}
                  className="hc-input"
                  value={formData.email}
                  onChange={handleChange("email")}
                  disabled
                />
              </div>
              <div className="hc-field">
                <label id="date-of-birth-label" htmlFor="date-of-birth-input" tabIndex={0} className="hc-label">Date of Birth</label>
                <Input
                  type="text"
                  id="date-of-birth-input"
                  aria-label="date of birth"
                  tabIndex={0}
                  className="hc-input"
                  value={formData.dateOfBirth}
                  onChange={handleChange("dateOfBirth")}
                  disabled
                />
              </div>
            </div>
          </section>

          {/* Guardian Information */}
          {isMinor && (<section className="hc-section">
            <h2 id="guardian-information" tabIndex={0} className="hc-section-title">
              Guardian Information
              <span className="hc-badge">Required — Member is a minor</span>
            </h2>
            <div className="hc-grid-2">
              <div className="hc-field">
                <label id="guardian-name-label" htmlFor="guardian-name-input" tabIndex={0} className="hc-label">Guardian Name <span style={{ color: "#d93025" }}>*</span></label>
                <Input
                  ref={guardianNameRef}
                  type="text"
                  id="guardian-name-input"
                  aria-label="guardian name"
                  tabIndex={0}
                  className={`hc-input${showValidationErrors && isMinor && !formData.guardianName.trim() ? " hc-input-error" : ""}`}
                  placeholder="Full name"
                  value={formData.guardianName}
                  onChange={handleChange("guardianName")}
                  required={isMinor}
                />
              </div>
              <div className="hc-field">
                <label id="guardian-contact-label" htmlFor="guardian-contact-input" tabIndex={0} className="hc-label">Guardian Contact Number <span style={{ color: "#d93025" }}>*</span></label>
                <Input
                  ref={guardianContactRef}
                  type="text"
                  id="guardian-contact-input"
                  aria-label="guardian contact number"
                  tabIndex={0}
                  className={`hc-input${showValidationErrors && isMinor && !formData.guardianContact.trim() ? " hc-input-error" : ""}`}
                  placeholder="(555) 555-0123"
                  value={formData.guardianContact}
                  onChange={handleChange("guardianContact")}
                  required={isMinor}
                />
              </div>
            </div>
          </section>)}

          {/* Primary Care Physician */}
          <section className="hc-section">
            <h2 id="pcp-information" tabIndex={0} className="hc-section-title">Primary Care Physician (PCP)</h2>
            <div className="hc-grid-2">
              <div className="hc-field">
                <label id="pcp-name-label" htmlFor="pcp-name-input" tabIndex={0} className="hc-label">PCP Name</label>
                <Input
                  type="text"
                  id="pcp-name-input"
                  aria-label="pcp name"
                  tabIndex={0}
                  className="hc-input"
                  value={formData.pcpName}
                  onChange={handleChange("pcpName")}
                  disabled
                />
              </div>
              <div className="hc-field">
                <label id="pcp-contact-label" htmlFor="pcp-contact-input" tabIndex={0} className="hc-label">PCP Contact Number</label>
                <Input
                  type="text"
                  id="pcp-contact-input"
                  aria-label="pcp contact number"
                  tabIndex={0}
                  className="hc-input"
                  value={formData.pcpContact}
                  onChange={handleChange("pcpContact")}
                  disabled
                />
              </div>
            </div>
          </section>

          {/* Book an Appointment */}
          <section className="hc-section">
            <h2 id="book-appointment" tabIndex={0} className="hc-section-title">Book an Appointment</h2>
            <div className="hc-booking-row">
              <div className="hc-field hc-date-field">
                <label id="select-date-label" htmlFor="select-date-input" tabIndex={0} className="hc-label">Select Date</label>
                <Input
                  type="date"
                  id="select-date-input"
                  tabIndex={0}
                  className="hc-input"
                  value={selectedDate}
                  min={minDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
              <div className="hc-slots-field">
                <label id="time-slots-label" htmlFor="time-slots-input" tabIndex={0} className="hc-label">
                  Time Slots (9 AM – 5 PM, 2-hour windows)
                </label>
                <div className="hc-slots-row">
                  {TIME_SLOTS.map((slot, idx) => {
                    const active = selectedSlots.includes(idx);
                    return (
                      <Button
                        htmlType="button"
                        key={slot}
                        id={`time-slot-${idx}`}
                        tabIndex={0}
                        className={"hc-slot-btn" + (active ? " hc-slot-btn-active" : "")}
                        onClick={() => {
                          setSelectedSlots((prev) =>
                            prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
                          );
                        }}
                      >
                        {slot}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="hc-add-row" id="add-selection-row" tabIndex={0}>
              <Button
                htmlType="button"
                className="hc-btn-primary"
                disabled={appointments.length >= MAX_SELECTIONS}
                onClick={handleAddSelection}
                id="add-selection-btn"
                tabIndex={0}
              >
                Add Selection
              </Button>
              <span className="hc-add-count">
                {appointments.length} / {MAX_SELECTIONS} dates added
              </span>
            </div>
            <div className="hc-table-wrap">
              <table className="hc-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>DATE</th>
                    <th>SELECTED TIME SLOTS</th>
                    <th className="hc-table-action">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="hc-empty-row">
                        No appointment selections added yet.
                      </td>
                    </tr>
                  ) : (
                    appointments.map((appt, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{appt.date}</td>
                        <td>
                          {appt.slots && appt.slots.length > 0
                            ? appt.slots.map((s, i) => (
                              <span key={i} className="hc-slot-badge">
                                {s}
                              </span>
                            ))
                            : null}
                        </td>
                        <td className="hc-table-action">
                          <Button
                            htmlType="button"
                            className="hc-remove-btn"
                            onClick={() => handleRemove(idx)}
                            tabIndex={0}
                            id={`remove-${idx}`}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Authorization */}
          <div className="hc-authorize-row" id="authorize-row" tabIndex={0}>
            <Input
              type="checkbox"
              id="hc-authorize"
              className="hc-checkbox"
              checked={authorized}
              onChange={(e) => setAuthorized(e.target.checked)}
              tabIndex={0}
            />
            <label htmlFor="hc-authorize" id="authorize-label" tabIndex={0} className="hc-authorize-text">
              I authorize HealthPlan to contact the healthcare provider to
              schedule an appointment.
              <br />
              HealthPlan will securely share your name, date of birth, and
              contact information with your provider's office. Your
              information is protected by strict privacy and security
              safeguards.
              <br />
              Once your appointment is confirmed, HealthPlan will send the
              details to you.
            </label>
          </div>

          <div className="hc-submit-row" id="submit-appointment-request" tabIndex={0}>
            <Button
              htmlType="button"
              className="hc-btn-submit"
              disabled={!authorized || submitting}
              onClick={handleSubmit}
              tabIndex={0}
              id="submit-request-btn"
            >
              Submit Appointment Request
            </Button>
          </div>
        </div>
      </Spin>
    </div>
  );
}